import { create } from 'zustand';
import type { DayOfWeek, AcademicYear, SchedulePeriod, Class, ClassSectionWithRelations } from '@timetable/types';

export type ViewMode = 'class' | 'teacher' | 'subject';

interface Conflict {
  type: 'teacher' | 'class' | 'unavailability';
  message: string;
  conflictingRecord?: unknown;
}

interface PeriodSlot {
  sectionId: string;
  periodId: string;
  day: DayOfWeek;
}

interface TeacherSuggestion {
  teacher: unknown; // Will be typed properly when integrated with API
  score: number;
  rank: number;
  reasons: string[];
  availability: 'available' | 'busy' | 'unavailable';
  workload: {
    current: number;
    max: number;
    percentage: number;
  };
}

interface TimetableDesignerState {
  viewMode: ViewMode;
  selectedEntity: string | null;
  selectedWeek: Date;
  conflicts: Conflict[];
  selectedDay: DayOfWeek | null;
  selectedPeriod: SchedulePeriod | null;
  temporalDate: Date | null; // Date for viewing historical/future timetable
  activeAcademicYear: AcademicYear | null; // Active academic year for temporal versioning

  // Multi-section state (Requirements 3.1, 3.2)
  editMode: boolean; // View Mode vs Edit Mode toggle
  selectedClass: Class | null; // Currently selected class for multi-section view
  selectedSections: ClassSectionWithRelations[]; // All sections of selected class
  selectedPeriodSlot: PeriodSlot | null; // Currently selected period slot for editing
  teacherSuggestions: TeacherSuggestion[]; // Intelligent teacher suggestions for selected slot
  helpMode: boolean; // "What's This?" mode for contextual help
}

interface TimetableDesignerActions {
  setViewMode: (mode: ViewMode) => void;
  selectEntity: (id: string | null) => void;
  setWeek: (date: Date) => void;
  setConflicts: (conflicts: Conflict[]) => void;
  clearConflicts: () => void;
  selectPeriod: (day: DayOfWeek | null, period: SchedulePeriod | null) => void;
  setTemporalDate: (date: Date | null) => void;
  setActiveAcademicYear: (year: AcademicYear | null) => void;

  // Multi-section actions (Requirements 3.1, 3.2)
  setEditMode: (enabled: boolean) => void;
  setSelectedClass: (classObj: Class | null) => void;
  setSelectedSections: (sections: ClassSectionWithRelations[]) => void;
  setSelectedPeriodSlot: (slot: PeriodSlot | null) => void;
  setTeacherSuggestions: (suggestions: TeacherSuggestion[]) => void;
  toggleHelpMode: () => void;
}

type TimetableDesignerStore = TimetableDesignerState & TimetableDesignerActions;

export const useTimetableDesignerStore = create<TimetableDesignerStore>()(
  (set) => ({
    viewMode: 'class',
    selectedEntity: null,
    selectedWeek: new Date(),
    conflicts: [],
    selectedDay: null,
    selectedPeriod: null,
    temporalDate: null,
    activeAcademicYear: null,

    // Multi-section state initial values
    editMode: false,
    selectedClass: null,
    selectedSections: [],
    selectedPeriodSlot: null,
    teacherSuggestions: [],
    helpMode: false,

    setViewMode: (mode) => set({ viewMode: mode }),
    selectEntity: (id) => set({ selectedEntity: id }),
    setWeek: (date) => set({ selectedWeek: date }),
    setConflicts: (conflicts) => set({ conflicts }),
    clearConflicts: () => set({ conflicts: [] }),
    selectPeriod: (day, period) => set({ selectedDay: day, selectedPeriod: period }),
    setTemporalDate: (date) => set({ temporalDate: date }),
    setActiveAcademicYear: (year) => set({ activeAcademicYear: year }),

    // Multi-section actions
    setEditMode: (enabled) => set({ editMode: enabled }),
    setSelectedClass: (classObj) => set({ selectedClass: classObj }),
    setSelectedSections: (sections) => set({ selectedSections: sections }),
    setSelectedPeriodSlot: (slot) => set({ selectedPeriodSlot: slot }),
    setTeacherSuggestions: (suggestions) => set({ teacherSuggestions: suggestions }),
    toggleHelpMode: () => set((state) => ({ helpMode: !state.helpMode })),
  })
);
